import { ITemplate, ITemplateState, IStringValue } from "../../types/types";
import { getInfoJsonWithArray, transformHelpUrl } from "../../utilities/utilities";
import { Theme, Button, MenuItem, TextField, FormControl, IconButton, InputAdornment, InputLabel, Tooltip } from "@mui/material";
import React, { useState, useRef, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import makeStyles from "@mui/styles/makeStyles";
import { refreshAll } from "./template.actions";
import { Typography, Select } from "@mui/material";
import TemplateListGrid from "./TemplateListGrid";
import { TemplateTabTitle, Strings } from "../../types/enums";
import { TECHNICAL_ERROR_MESSAGE } from "../../constants/CommonConstants";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from '@mui/icons-material/Search';
import Overlay from 'react-bootstrap/Overlay';
import Popover from 'react-bootstrap/Popover';
import { UserContext, UserContextType } from '../../modules/User/UserContext';
import { EpiqUser, TemplatePermissions } from '../../constants/roles';
import { useLocation } from 'react-router-dom';
import ReactGA from "react-ga4";
import NewTemplateDialog from "./NewTemplateDialog";
import TemplateEditorDialog from "./TemplateEditorDialog";

const useStyles = makeStyles((theme: Theme) => ({
	fab: {
		position: "absolute",
		bottom: theme.spacing(5),
		right: theme.spacing(5),
	},
	button: {
		background: "#006B86",
		size: "small",
		margin: theme.spacing(1),
		color: "white",
	},
	textLink: {
		color: theme.palette.text.primary,
		padding: "0.5em",
	},
	commonbackgroundcard: {
		boxShadow: "0px 10px 15px -3px rgb(0 0 0 / 30%)",
		padding: "1.5rem 1rem",
		margin: "1rem 1rem 0 0",
		background: "#fff",
		borderRadius: "5px",
	},
	title: {
		flexGrow: 1,
		marginBottom: "15px",
		display: 'inline-flex',
		marginRight: "5px"
	},
	dashboardIconBackground: {
		fontSize: "20px"
	},
	floatLeft: {
		float: 'left'
	},
	floatRight: {
		float: 'right',
	},
	headOuter: {
		display: 'inline-block',
		width: '100%',
	},
	searchName: {
		position: 'relative', top: '-10px'
	},

}));

const TemplateListPage = () => {
	const dispatch = useDispatch();
	const templateState: ITemplateState = useSelector((state: any) => state.templateState);
	const classes = useStyles();
	const { user, isInitialized, isAuthenticated, isInRole } = useContext(UserContext) as UserContextType;
	let [show, setShow] = useState(false);
	const [target, setTarget] = useState(null);
	const ref = useRef(null);
	const [stringContentData, setStringContentData] = useState<IStringValue | null>();

	let [selectedTemplate, setSelectedTemplate] = useState<ITemplate>();
	const [openDialog, setOpenDialog] = useState(false);
	const [copyTemplateId, setCopyTemplateId] = useState<number | null>(null);
	const [editTemplateId, setEditTemplateId] = useState<number | null>(null);
	const [openEditorDialog, setOpenEditorDialog] = useState(false);

	document.title = TemplateTabTitle.Title;
	const location = useLocation();

	const [wrkFilterName, setWrkFilterName] = useState<string>('');
	const [filterName, setFilterName] = useState<string>('');
	const [filterStatus, setFilterStatus] = useState<string>('all');

	useEffect(() => {
		const path = location.pathname + location.search;
		ReactGA.send({ hitType: "pageview", page: path, title: TemplateTabTitle.Title });
	}, [location])

	const doSearch = () => {
		setFilterName(wrkFilterName);
	}

	const selectTemplate = (templateId: number) => {
		const templateSelected = templateState.templates.find((x) => x.id === templateId);

		if (templateSelected) {
			setSelectedTemplate(templateSelected);
		}
	};

	const handleClick = (event: any) => {
		setShow(!show);
		setTarget(event.target);
	};

	const handleClear = () => {
		setWrkFilterName('');
		setFilterName('');
		setFilterStatus('all');
	}

	const processInfoResponse = (data: any) => {
		data.map((item: IStringValue) => {
			switch (item.key) {
				case Strings.TemplateList:
					item.value = transformHelpUrl(item.value, isInRole([EpiqUser]));
					setStringContentData(item);
					break;


				default:
					break;
			}
		});
	};

	useEffect(() => {
		const fetchInfoStrings = async () => {
			const data: any = await getInfoJsonWithArray([
				Strings.TemplateList,
			]);

			processInfoResponse(data);
		};


		if (isInitialized && isAuthenticated) {
			fetchInfoStrings();
		}
	}, [isAuthenticated, isInitialized]);

	const handleNewTemplateSaved = (newTemplate: ITemplate) => {
		setCopyTemplateId(null);
		setOpenDialog(false);
		setSelectedTemplate(newTemplate);
		setOpenEditorDialog(true);
	};

	const handleNewTemplateClosed = () => {
		setCopyTemplateId(null);
		setOpenDialog(false);
		setOpenEditorDialog(false);
		dispatch(refreshAll())
	};

	const handleDuplicateTemplate = (copyTemplateId: number | null) => {
		setCopyTemplateId(copyTemplateId);
		setOpenDialog(true);
	}

	const handleEditTemplate = (editTemplateId: number | null) => {
		setEditTemplateId(editTemplateId);
		setSelectedTemplate(undefined);
		setOpenEditorDialog(true);
	}

	return (
		<>
			<div className={classes.commonbackgroundcard}>
				<div className={classes.headOuter}>
					<div className={classes.floatLeft} ref={ref}>
						<Typography variant="h6" className={classes.title} noWrap>
							Claim Templates
						</Typography>

						<Button style={{ background: "none", outline: "none", padding: "0", position: "relative", bottom: "3px", justifyContent: "left", minWidth: "25px" }} onClick={handleClick}><InfoOutlinedIcon className={classes.dashboardIconBackground} /></Button>

						<Overlay
							show={show}
							target={target}
							placement="right-start"
							container={ref}
							containerPadding={20}
							rootClose
							onHide={() => setShow(false)}
						>
							<Popover id="popover-contained">
								<Popover.Header as="h3"><span style={{ color: "#000" }}>Claim Templates</span></Popover.Header>
								{stringContentData && (
									<Popover.Body dangerouslySetInnerHTML={{ __html: stringContentData.value }}></Popover.Body>
								)}
							</Popover>
						</Overlay>

					</div>
					<div style={{ position: 'relative', float: 'left', marginLeft: '20px' }}>
						<FormControl sx={{ m: 1, width: '25ch' }} variant="outlined" >
							<TextField
								label="Find"
								id="outlined-start-adornment"
								className={classes.searchName}
								value={wrkFilterName}
								InputLabelProps={{
									style: { color: 'black' }
								}}
								onChange={(e: any) => {
									setWrkFilterName(e.target.value);
								}}
								onKeyPress={(e: any) => {
									if (e.key === 'Enter') {
										doSearch();
									}
								}}
								onBlur={() => {
									doSearch();
								}}
								InputProps={{
									endAdornment: <InputAdornment position="end">
										<IconButton
											onClick={() => {
												doSearch();
											}}
										>
											<SearchIcon />
										</IconButton></InputAdornment>,

								}}
							/>
						</FormControl>
					</div>




					<div className={classes.floatRight}>
						<Button
							variant="contained"
							disabled={!isInRole(TemplatePermissions.Add)}
							className={classes.button}
							onClick={() => setOpenDialog(true)}
							sx={{
								"&:focus": {
									backgroundColor: "#253746 !important", // Keep the default background color on focus
								},
							}}>
							+ ADD
						</Button>
						<Tooltip title="Clear search result">
							<IconButton onClick={handleClear} size="large" disabled={filterName === '' && filterStatus === 'all'}>
								<CloseIcon />
							</IconButton>
						</Tooltip>
					</div>

					<div
						className={classes.floatRight}
						style={{ marginRight: '2rem' }}
					>
						<FormControl fullWidth variant="outlined" >
							<InputLabel id="demo-simple-select-label" style={{ color: 'black' }}>Status</InputLabel>
							<Select
								autoWidth
								labelId="demo-simple-select-label"
								id="demo-simple-select"
								style={{ width: '150px' }}
								value={filterStatus}
								label="Status"
								onChange={(e: any) => {
									let val = e.target.value;

									setFilterStatus(e.target.value);
								}}
							>
								<MenuItem value="all">All</MenuItem>
								<MenuItem value="draft">Draft</MenuItem>
								<MenuItem value="approved">Approved</MenuItem>
								<MenuItem value="in use">In Use</MenuItem>
							</Select>
						</FormControl>
					</div>
				</div>

				<TemplateListGrid
					selectTemplate={selectTemplate}
					searchName={filterName}
					searchStatus={filterStatus}
					handleDuplicateTemplate={handleDuplicateTemplate}
					handleEditTemplate={handleEditTemplate}
				/>

				{templateState.error && <p style={{ color: 'red' }}>{TECHNICAL_ERROR_MESSAGE}</p>}
			</div>

			<NewTemplateDialog open={openDialog} onSave={handleNewTemplateSaved} onClose={handleNewTemplateClosed} copyTemplateId={copyTemplateId} />
			<TemplateEditorDialog templateId={selectedTemplate ? selectedTemplate?.id : ( editTemplateId ? editTemplateId : 0)} open={openEditorDialog} onClose={handleNewTemplateClosed} />
		</>
	);
};

export default TemplateListPage;
